import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {ExperienceType} from '@taopix/taopix-core/dist/types/types';
import {useTaopix} from '../Context/TaopixContext';
import {AIMode, AssistantType} from '@taopix/taopix-core';
import {LayoutSelector} from '../../Selectors';

type ExperienceState = {
  experience: ExperienceType;
  updateExperience: React.Dispatch<React.SetStateAction<ExperienceType>>;
  getAIMode: () => AIMode;
};

export const ExperienceContext = createContext<ExperienceState>({} as ExperienceState);

const ExperienceProvider = ({children}: {children: any}) => {
  const app = useTaopix();
  const productLayout = useSelector(LayoutSelector);
  const [experience, updateExperience] = useState<ExperienceType>({} as ExperienceType);

  useEffect(() => {
    app.core.get('loader').on('onLoad', () => {
      updateExperience(app.core.get('experience') as unknown as ExperienceType);
    });
    app.core.get('loader').on('on3DPreviewLoad', () => {
      updateExperience(app.core.get('experience') as unknown as ExperienceType);
    });
    app.core.get('loader').on('onAssistantLoad', () => {
      updateExperience(app.core.get('experience') as unknown as ExperienceType);
    });
  }, [app.core.get('loader')]);

  const getAIMode = useCallback(() => {
    return (() => {
      if (!experience.hasOwnProperty('WIZARD')) return AIMode.NotAvailable;
      if (AssistantType.SmartDesign === experience.WIZARD.designAssistantType.designAssistantType) {
        // Use the setting on the product layout, fallback to optional if not set (i.e. might be in the Assistant).
        return typeof productLayout?.features?.aimode === 'undefined'
          ? AIMode.Off
          : productLayout?.features?.aimode;
      }

      return AIMode.NotAvailable;
    })();
  }, [experience, productLayout?.features?.aimode]);

  return (
    <ExperienceContext.Provider value={{updateExperience, experience, getAIMode}}>
      {children}
    </ExperienceContext.Provider>
  );
};

export const useExperience = () => {
  const context = useContext(ExperienceContext);
  if (context === undefined) {
    throw new Error('useExperience must be used within an Experience Provider');
  }
  return context;
};

export default ExperienceProvider;
